import { Grid } from '@mui/material';
import { set } from 'lodash';
import type { Path } from 'react-hook-form';
import {
  ColorPickerWidget,
  NumberInputWidget,
  SelectDropdownWidget,
} from 'src/components/widget';
import { FONT_FAMILY_OPTIONS } from 'src/constants';
import { useGetProjectConfig } from 'src/hooks';
import { type IGcfBadgeConfig, OrderLabelEnum } from 'src/interfaces';
import { lang } from 'src/utils';

import {
  badgeTypeOptions,
  generateAlphabetOptions,
  generateSelectNumberOptions,
} from '../helper';
import type { GroupBadgeControllerProps } from './GroupBadgeController.interface';

export const GroupBadgeController = (props: GroupBadgeControllerProps) => {
  const { data: projectConfig } = useGetProjectConfig();

  const projectFonts =
    projectConfig?.fonts?.map((item) => ({
      label: item.name,
      value: item.name,
    })) ?? [];
  const mappedFonts =
    projectFonts.length > 0 ? projectFonts : FONT_FAMILY_OPTIONS;

  const handleChange = (field: Path<IGcfBadgeConfig>) => (value: any) => {
    const updatedValue = set(
      { ...props.value },
      field,
      value,
    ) as IGcfBadgeConfig;

    if (field === 'type') {
      if (value === OrderLabelEnum.number) {
        updatedValue.startFrom = '1';
      } else if (value === OrderLabelEnum.alphabet) {
        updatedValue.startFrom = '0';
      }
    }

    props.handleChange(updatedValue);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <SelectDropdownWidget
          label={lang.type}
          value={props.value?.type}
          hasError={!!props?.errors?.type}
          error={props?.errors?.type?.message || false}
          selectOptions={badgeTypeOptions}
          onChange={handleChange('type')}
        />
      </Grid>

      <Grid item xs={6}>
        <SelectDropdownWidget
          label={lang.startFrom}
          value={props.value?.startFrom}
          hasError={!!props?.errors?.startFrom}
          error={props?.errors?.startFrom?.message || false}
          selectOptions={
            props.value?.type === OrderLabelEnum.alphabet
              ? generateAlphabetOptions()
              : generateSelectNumberOptions(1, 20)
          }
          onChange={handleChange('startFrom')}
        />
      </Grid>

      <Grid item xs={6}>
        <SelectDropdownWidget
          label={lang.fontFamily}
          value={
            props.value?.style?.font ??
            props.currentTheme?.config?.badges?.groupBadge?.font
          }
          hasError={!!props.errors?.style?.font}
          error={props.errors?.style?.font?.message || false}
          selectOptions={mappedFonts}
          onChange={handleChange('style.font')}
          options={{ suffix: 'px' }}
        />
      </Grid>

      <Grid item xs={6}>
        <NumberInputWidget
          label={lang.fontSize}
          value={
            props.value?.style?.fontSize ??
            props.currentTheme?.config?.badges?.groupBadge?.fontSize
          }
          hasError={!!props.errors?.style?.fontSize}
          error={props.errors?.style?.fontSize?.message || false}
          onChange={handleChange('style.fontSize')}
          options={{ suffix: 'px' }}
        />
      </Grid>

      <Grid item xs={6}>
        <ColorPickerWidget
          label={lang.fontColor}
          value={
            props.value?.style?.color ??
            props.currentTheme?.config?.badges?.groupBadge?.color
          }
          hasError={!!props?.errors?.style?.color}
          error={props?.errors?.style?.color?.message || false}
          colorPickerMode="block"
          options={{ triangle: 'hide' }}
          pickerWrapperStyle={{ left: '0' }}
          onChange={handleChange('style.color')}
        />
      </Grid>

      <Grid item xs={6}>
        {/* lineHeight is unresponsive with GCF as it has hardcoded in GCF, keeping this input box make sure we don't lose any functionality */}
        <NumberInputWidget
          label={lang.lineHeight}
          value={
            props.value?.style?.lineHeight ??
            props.currentTheme?.config.badges?.groupBadge?.lineHeight
          }
          hasError={!!props.errors?.style?.lineHeight}
          error={props.errors?.style?.lineHeight?.message || false}
          onChange={handleChange('style.lineHeight')}
          options={{ suffix: 'px' }}
        />
      </Grid>
    </Grid>
  );
};
